/**
 * หน้าจอ Delivery (P2-9g) — โหลดกล่องขึ้นรถ สแกนออกจากประตู (Dispatch) และถอนการ
 * ขายเมื่อส่งของผิด (D-105) — ครึ่งหลังของงานส่งของ ต่อจากหน้าจอ Packing
 * (`WO-P2-M9-006`)
 *
 * โครงยืมมาจาก `PackingScreen` โดยตั้งใจ (invariant "COPY PackingScreen's SHAPE,
 * NOT ITS FILES"): ช่องกรองบนสุด ตารางรายการ ปุ่ม lifecycle รายแถว และหลังสำเร็จก็
 * โหลดรายการใหม่ผ่าน client เส้นเดิม — แต่ `lib/packing/**` และ `PackingScreen.tsx`
 * เป็น forbidden_scope ของ Work Order นี้ ไฟล์นี้และ `lib/deliveries/**` จึงเป็น
 * feature folder ใหม่ทั้งชุด ไม่ได้ import จากฝั่งนั้นเลย
 *
 * หน้าจอนี้มีสองตาราง เพราะสองทรัพยากรตอบคำถามคนละข้อ:
 *
 * 1. **กล่องที่รอโหลด/ส่ง** (`GET /v1/packing-units`) — โหลดขึ้นรถ (`load`, EDIT)
 *    เสนอเฉพาะกล่องที่ `PACKED` และส่งออกจากประตู (`POST /v1/deliveries`, CREATE)
 *    เสนอเฉพาะกล่องที่ `LOADED` เท่านั้น **THE THREE STATES ARE NOT THE SAME ACT**:
 *    `PACKED` ยังเป็นสต๊อกของโรงงาน, `LOADED` ก็ยังเป็น (อยู่บนรถแต่ในประตู),
 *    มีแต่การส่ง (`DISPATCHED`) เท่านั้นที่ของออกจากบริษัทจริง
 * 2. **รายการเดลิเวอรี** (`GET /v1/deliveries`) — ถอนการขาย (`reverse`, APPROVE)
 *    ต้องกรอกเหตุผลก่อนปุ่มจะเปิด และเดลิเวอรีที่ถูกถอนแล้วยังคงแสดงอยู่ในรายการ
 *    พร้อมเครื่องหมาย ไม่ถูกซ่อน — `shipped_qty` ไม่ขยับไม่ว่ากรณีใด
 *
 * **THE PERMISSION HEADLINE**: `load` ต้องการ `SALES` + EDIT, ส่งออก
 * (`POST /v1/deliveries`) ต้องการ `SALES` + **CREATE** และถอนการขายต้องการ
 * `SALES` + **APPROVE** — คนละสิทธิ์กับการส่งโดยตั้งใจ (D-105 ข้อ 6) พนักงานที่ส่ง
 * ของออกไปได้ไม่ได้แปลว่าถอนการขายได้ ปุ่มที่ขาดสิทธิ์ยังคงแสดง แต่กดไม่ได้พร้อม
 * เหตุผล เหมือนหน้าจอ Packing — ตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก
 * request เสมอ (`references/authorization-rbac.md` ข้อ 5)
 */

import { useEffect, useState } from 'react';

import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  createDelivery,
  fetchDeliveries,
  fetchPackingUnits,
  loadPackingUnit,
  reverseDelivery,
  ruleFromFailure,
  type DeliveryFailure,
  type DeliveryRequestOptions,
} from '../../lib/deliveries/client';
import {
  deliveryActionLabel,
  deliveryActionPermission,
  deliveryLifecycleActionFor,
  isValidReversalReason,
  packingUnitStatusLabel,
  packingUnitStatusTone,
  toCustomerCodeFilter,
  toSoNumberFilter,
  type DeliveryLifecycleAction,
} from '../../lib/deliveries/deliveryView';
import {
  CUSTOMER_CODE_MAX_LENGTH,
  DELIVERY_ALREADY_REVERSED,
  DELIVERY_FIELD_IS_SERVER_OWNED,
  DELIVERY_MODULE,
  DELIVERY_NOT_FOUND,
  LOT_NOT_RELEASED,
  NO_ACCOUNT_MAPPING,
  NO_UNIT_COST,
  PACKING_UNIT_FIELD_IS_SERVER_OWNED,
  PACKING_UNIT_NOT_FOUND,
  PACKING_UNIT_STATUS_CHANGED,
  PACKING_UNIT_TRANSITION_NOT_ALLOWED,
  PLANT_OUTSIDE_SCOPE,
  REVERSAL_REASON_MAX_LENGTH,
  SALES_ORDER_NOT_DELIVERABLE,
  SHIPPING_MORE_THAN_ORDERED,
  SO_NUMBER_MAX_LENGTH,
  type Delivery as DeliveryResource,
  type DeliverySummary,
  type PackingUnitSummary,
} from '../../lib/deliveries/types';

/* ------------------------------------------------- หน้าจอความล้มเหลว ----- */

type FailureContext = 'packing-units' | 'deliveries' | DeliveryLifecycleAction | 'reverse';

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
}

function nothingChangedText(context: FailureContext): string {
  switch (context) {
    case 'load':
      return ' กล่องนี้ยังไม่ถูกโหลดขึ้นรถ และไม่มีแถว audit ถูกเขียน';
    case 'dispatch':
      return ' ไม่มีอะไรออกจากประตู ไม่มี inv_ledger ถูกเขียน และไม่มีเอกสารบัญชีถูกโพสต์ — การส่งเป็นธุรกรรมเดียว';
    case 'reverse':
      return ' การขายนี้ยังไม่ถูกถอน ไม่มี inv_ledger ถูกเขียน และไม่มีเอกสารบัญชีถูกโพสต์ — การถอนเป็นธุรกรรมเดียว';
    case 'packing-units':
    case 'deliveries':
      return '';
  }
}

function isWriteContext(context: FailureContext): boolean {
  return nothingChangedText(context) !== '';
}

/**
 * ข้อความของกฎแต่ละข้อ — คืน `null` เมื่อไม่ใช่กฎที่ router นี้ตอบจริง
 *
 * ⚠️ ตัวเลขที่แม่นยำ (สถานะปัจจุบัน, ผู้กลับรายการ, เวลา ฯลฯ) มาจาก `details` ที่
 * แสดงเป็นรายการแยกด้านล่างข้อความนี้เสมอ — ข้อความตรงนี้อธิบาย**กฎ** ส่วนตัวเลขจริง
 * มาจากเซิร์ฟเวอร์โดยตรง ไม่ใช่ค่าที่หน้าจอคำนวณเอง
 */
function ruleCopy(rule: string | null): FailureCopy | null {
  switch (rule) {
    case PLANT_OUTSIDE_SCOPE:
      return {
        heading: 'โรงงานของกล่อง/เดลิเวอรีนี้อยู่นอกขอบเขตของคุณ',
        text: `เซิร์ฟเวอร์ตอบ 403 พร้อมกฎ ${PLANT_OUTSIDE_SCOPE} — โหลด/ส่งออก/ถอนการขายเป็นการเขียนเข้าโรงงานหนึ่ง (D-004)`,
      };
    case PACKING_UNIT_NOT_FOUND:
      return {
        heading: 'ไม่พบกล่องเลขนี้',
        text: `เซิร์ฟเวอร์ตอบ 404 พร้อมกฎ ${PACKING_UNIT_NOT_FOUND} — เลขกล่องอาจพิมพ์ผิดหรือกล่องถูกลบไปแล้ว โหลดรายการใหม่แล้วลองอีกครั้ง`,
      };
    case PACKING_UNIT_TRANSITION_NOT_ALLOWED:
      return {
        heading: 'กล่องนี้อยู่ในสถานะที่กดสิ่งนี้ไม่ได้',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${PACKING_UNIT_TRANSITION_NOT_ALLOWED} — สถานะปัจจุบันจริงและสถานะปลายทางอยู่ในรายละเอียดด้านล่าง โหลดรายการใหม่เพื่อดูสถานะจริงก่อนลองอีกครั้ง`,
      };
    case PACKING_UNIT_STATUS_CHANGED:
      return {
        heading: 'กล่องนี้เปลี่ยนไปตั้งแต่ตอนที่อ่านมา',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${PACKING_UNIT_STATUS_CHANGED} — โหลดรายการใหม่เพื่อดูสถานะจริง`,
      };
    case SALES_ORDER_NOT_DELIVERABLE:
      return {
        heading: 'ใบสั่งขายของกล่องนี้ถูกยกเลิกไปแล้ว',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${SALES_ORDER_NOT_DELIVERABLE} — ใบสั่งขายถูกยกเลิกระหว่างที่กล่องรอส่ง สถานะจริงของใบอยู่ในรายละเอียดด้านล่าง`,
      };
    case LOT_NOT_RELEASED:
      return {
        heading: 'มี lot ในกล่องนี้ไม่ใช่ RELEASED อีกแล้ว',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${LOT_NOT_RELEASED} — lot ถูกกักคุณภาพระหว่างที่กล่องรอส่ง (ตรวจอีกครั้งที่ประตู) ส่งไม่ได้จนกว่าจะปลดกัก`,
      };
    case SHIPPING_MORE_THAN_ORDERED:
      return {
        heading: 'การส่งนี้จะเกินจำนวนที่ลูกค้าสั่ง',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${SHIPPING_MORE_THAN_ORDERED} — ไม่มี tolerance ให้ส่งเกิน ตัวเลขที่สั่ง/ส่งไปแล้ว/ส่วนเกินอยู่ในรายละเอียดด้านล่าง`,
      };
    case NO_ACCOUNT_MAPPING:
      return {
        heading: 'ยังไม่ได้ตั้งค่าบัญชีสำหรับการส่งของนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${NO_ACCOUNT_MAPPING} — ต้องตั้งค่า Account Determination ให้ประเภทการเคลื่อนไหวนี้ในบริษัทนี้ก่อน ไม่มีอะไรถูกโพสต์`,
      };
    case NO_UNIT_COST:
      return {
        heading: 'สินค้านี้ไม่มีต้นทุนต่อหน่วยในโรงงานนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${NO_UNIT_COST} — ตีมูลค่าไม่ได้จึงยังไม่ส่ง (ห้ามโพสต์ด้วยค่าศูนย์) ต้องตั้งต้นทุนก่อน`,
      };
    case DELIVERY_ALREADY_REVERSED:
      return {
        heading: 'เดลิเวอรีนี้ถูกถอนการขายไปแล้ว',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${DELIVERY_ALREADY_REVERSED} — **ไม่ใช่ replay** เวลาที่ถอนและผู้ที่ถอนจริงอยู่ในรายละเอียดด้านล่าง ถอนซ้ำไม่ได้โดยตั้งใจ`,
      };
    case DELIVERY_NOT_FOUND:
      return {
        heading: 'ไม่พบเดลิเวอรีนี้',
        text: `เซิร์ฟเวอร์ตอบ 404 พร้อมกฎ ${DELIVERY_NOT_FOUND} — เดลิเวอรีนี้ไม่มีอยู่แล้ว หรืออยู่ในโรงงานนอกขอบเขตของคุณ`,
      };
    case DELIVERY_FIELD_IS_SERVER_OWNED:
    case PACKING_UNIT_FIELD_IS_SERVER_OWNED:
      return {
        heading: 'คำขอนี้ส่งฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของ',
        text: `เซิร์ฟเวอร์ตอบ 400 — นี่ไม่ควรเกิดจากหน้าจอนี้ แจ้งผู้ดูแลระบบ`,
      };
    default:
      return null;
  }
}

function currentStatusOf(failure: DeliveryFailure): string | null {
  if (!('error' in failure)) {
    return null;
  }
  const value = failure.error.details.current_status;
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function conflictCopy(currentStatus: string | null): FailureCopy {
  return {
    heading:
      currentStatus === null
        ? 'สถานะปัจจุบันของกล่องนี้ไม่รับสิ่งที่กด'
        : `กล่องนี้อยู่ที่ ${packingUnitStatusLabel(currentStatus)} แล้ว`,
    text: 'เซิร์ฟเวอร์ตอบ 409 — โหลดรายการใหม่เพื่อดูสถานะจริงก่อนลองอีกครั้ง',
  };
}

function failureCopy(failure: DeliveryFailure, context: FailureContext): FailureCopy {
  const writing = isWriteContext(context);
  const nothingChanged = nothingChangedText(context);

  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้',
        text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว (D-049)',
      };
    case 'forbidden':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'สิทธิ์ชุดนี้ไม่ครอบคลุมคำขอนี้',
          text: `เซิร์ฟเวอร์ตอบ 403 — คำขอนี้ต้องการโมดูล ${DELIVERY_MODULE} ในระดับที่สูงกว่าที่แผนกของคุณถืออยู่${nothingChanged}`,
        }
      );
    case 'not-found':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'ไม่พบสิ่งที่คำขอนี้อ้างถึง',
          text: `เซิร์ฟเวอร์ตอบ 404 — โหลดรายการใหม่แล้วลองอีกครั้ง${nothingChanged}`,
        }
      );
    case 'conflict':
      return ruleCopy(failure.error.rule) ?? conflictCopy(currentStatusOf(failure));
    case 'rule-violation':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำขอนี้ติดกฎทางธุรกิจ',
          text: `เซิร์ฟเวอร์ตอบ 422 พร้อมรหัสกฎที่ชน — ไม่ใช่ข้อผิดพลาดของระบบ${nothingChanged}`,
        }
      );
    case 'bad-request':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
          text: `เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ${nothingChanged}`,
        }
      );
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text: writing
          ? 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ และไม่มีอะไรยืนยันว่าข้อมูลถูกเปลี่ยนหรือไม่ — โหลดรายการใหม่ก่อนลองอีกครั้ง'
          : 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text: writing
          ? `${failure.detail} — ไม่มีอะไรยืนยันว่าคำขอไปถึงเซิร์ฟเวอร์หรือไม่ โหลดรายการใหม่ก่อนลองอีกครั้ง`
          : `${failure.detail} — หน้าจอนี้จึงไม่มีข้อมูลจะแสดง`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงข้อมูลที่อ่านได้ไม่ครบ',
      };
    case 'cancelled':
      return {
        heading: 'ยกเลิกการเรียกข้อมูลแล้ว',
        text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ',
      };
  }
}

function envelopeOf(failure: DeliveryFailure) {
  return 'error' in failure ? failure.error : null;
}

function detailEntries(envelope: NonNullable<ReturnType<typeof envelopeOf>>): readonly (readonly [
  string,
  string,
])[] {
  return Object.entries(envelope.details)
    .filter(([key]) => key !== 'rule')
    .map(([key, value]) => [key, formatDetailValue(value)] as const);
}

function formatDetailValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map((entry) => formatDetailValue(entry)).join(', ');
  }
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

interface FailureNoticeProps {
  readonly failure: DeliveryFailure;
  readonly context: FailureContext;
  readonly onRetry?: () => void;
  readonly testIdPrefix: string;
}

function FailureNotice({ failure, context, onRetry, testIdPrefix }: FailureNoticeProps) {
  const copy = failureCopy(failure, context);
  const envelope = envelopeOf(failure);
  const rule = ruleFromFailure(failure);
  const serverMessage = envelope === null ? null : envelope.message;
  const entries = envelope === null ? [] : detailEntries(envelope);
  const refusedByRule = failure.status === 'rule-violation' || failure.status === 'conflict';

  return (
    <div
      className="cap-notice"
      data-testid={`${testIdPrefix}-failure`}
      data-failure={failure.status}
      data-context={context}
      data-rule={rule ?? ''}
      role="status"
    >
      <StatusBadge tone={refusedByRule ? 'warning' : 'danger'}>
        {refusedByRule ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>

      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน:{' '}
          <code className="cap-table__code" data-testid={`${testIdPrefix}-rule`}>
            {rule}
          </code>
        </p>
      )}

      {serverMessage === null ? null : (
        <p className="cap-notice__note" data-testid={`${testIdPrefix}-failure-message`}>
          ข้อความจากเซิร์ฟเวอร์: {serverMessage}
        </p>
      )}

      {entries.length === 0 ? null : (
        <dl className="cap-notice__details" data-testid={`${testIdPrefix}-failure-details`}>
          {entries.map(([key, value]) => (
            <div key={key} data-testid={`${testIdPrefix}-failure-detail-${key}`}>
              <dt>{key}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}

      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- หน้าจอ -- */

export interface DeliveryScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

type PackingUnitListState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly PackingUnitSummary[] }
  | DeliveryFailure;

type DeliveryListState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly DeliverySummary[] }
  | DeliveryFailure;

interface PackingActionOutcome {
  readonly packingUnitId: string;
  readonly action: DeliveryLifecycleAction;
  readonly failure: DeliveryFailure | null;
}

interface ReverseOutcome {
  readonly deliveryId: string;
  readonly failure: DeliveryFailure | null;
  readonly delivery: DeliveryResource | null;
}

interface PackingRowActionProps {
  readonly item: PackingUnitSummary;
  readonly action: DeliveryLifecycleAction;
  readonly permissions: PermissionsState;
  readonly busy: boolean;
  readonly pendingHere: boolean;
  readonly onRun: () => void;
}

/** ปุ่ม `load`/`dispatch` ของหนึ่งแถว แยกเป็น component ของตัวเองเพื่อให้ `action`
 * (แคบลงจาก `DeliveryLifecycleAction | null` แล้วในผู้เรียก) เป็น prop ที่ประกาศ
 * ชนิดไม่เป็น `null` ตรง ๆ แทนการพึ่ง TypeScript ให้จำการแคบชนิดข้ามขอบเขต closure */
function PackingRowAction({
  item,
  action,
  permissions,
  busy,
  pendingHere,
  onRun,
}: PackingRowActionProps) {
  const permission = deliveryActionPermission(action);
  const allowed = can(permissions, DELIVERY_MODULE, permission);

  return (
    <span>
      <button
        type="button"
        className="cap-button cap-button--ghost"
        data-testid={`dv-action-${action}-${item.package_no}`}
        disabled={busy || !allowed}
        title={allowed ? undefined : `ต้องมีสิทธิ์ ${DELIVERY_MODULE} + ${permission}`}
        onClick={onRun}
      >
        {pendingHere ? 'กำลังส่ง…' : deliveryActionLabel(action)}
      </button>
      {allowed ? null : (
        <span
          className="cap-field__hint"
          data-testid={`dv-action-reason-${action}-${item.package_no}`}
        >
          ต้องมีสิทธิ์ {DELIVERY_MODULE} + {permission}
        </span>
      )}
    </span>
  );
}

export function DeliveryScreen({ permissions, apiBaseUrl, fetchImpl }: DeliveryScreenProps) {
  const canLoad = can(permissions, DELIVERY_MODULE, 'EDIT');
  const canDispatch = can(permissions, DELIVERY_MODULE, 'CREATE');
  const canReverse = can(permissions, DELIVERY_MODULE, 'APPROVE');
  const canWriteAnything = canLoad || canDispatch || canReverse;

  const [packingUnits, setPackingUnits] = useState<PackingUnitListState>({ status: 'loading' });
  const [deliveries, setDeliveries] = useState<DeliveryListState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  const [soNumberInput, setSoNumberInput] = useState('');
  const [soNumberFilter, setSoNumberFilter] = useState('');
  const [customerCodeInput, setCustomerCodeInput] = useState('');
  const [customerCodeFilter, setCustomerCodeFilter] = useState('');

  const [pendingAction, setPendingAction] = useState<
    | { readonly kind: 'load' | 'dispatch'; readonly packingUnitId: string }
    | { readonly kind: 'reverse'; readonly deliveryId: string }
    | null
  >(null);
  const [packingOutcome, setPackingOutcome] = useState<PackingActionOutcome | null>(null);
  const [reverseOutcome, setReverseOutcome] = useState<ReverseOutcome | null>(null);
  const [reasonDrafts, setReasonDrafts] = useState<Readonly<Record<string, string>>>({});

  const requestOptions: DeliveryRequestOptions = { apiBaseUrl, fetchImpl };

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setPackingUnits((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    const soNumber = toSoNumberFilter(soNumberFilter);
    void fetchPackingUnits(
      { ...(soNumber === undefined ? {} : { so_number: soNumber }) },
      { apiBaseUrl, fetchImpl, signal: controller.signal },
    ).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setPackingUnits(
        result.status === 'ready' ? { status: 'ready', items: result.page.items } : result,
      );
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken, soNumberFilter]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setDeliveries((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    const soNumber = toSoNumberFilter(soNumberFilter);
    const customerCode = toCustomerCodeFilter(customerCodeFilter);
    void fetchDeliveries(
      {
        ...(soNumber === undefined ? {} : { so_number: soNumber }),
        ...(customerCode === undefined ? {} : { customer_code: customerCode }),
      },
      { apiBaseUrl, fetchImpl, signal: controller.signal },
    ).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setDeliveries(
        result.status === 'ready' ? { status: 'ready', items: result.page.items } : result,
      );
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken, soNumberFilter, customerCodeFilter]);

  const runPackingAction = async (
    item: PackingUnitSummary,
    action: DeliveryLifecycleAction,
  ) => {
    const permission = deliveryActionPermission(action);
    if (pendingAction !== null || !can(permissions, DELIVERY_MODULE, permission)) {
      return;
    }

    setPendingAction({ kind: action, packingUnitId: item.packing_unit_id });
    setPackingOutcome(null);

    const result =
      action === 'load'
        ? await loadPackingUnit(item.packing_unit_id, requestOptions)
        : await createDelivery({ package_no: item.package_no }, requestOptions);

    setPendingAction(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setPackingOutcome({ packingUnitId: item.packing_unit_id, action, failure: result });
      return;
    }

    setPackingOutcome({ packingUnitId: item.packing_unit_id, action, failure: null });
    setReloadToken((token) => token + 1);
  };

  const runReverse = async (item: DeliverySummary) => {
    const reason = (reasonDrafts[item.delivery_id] ?? '').trim();
    if (
      pendingAction !== null ||
      !can(permissions, DELIVERY_MODULE, 'APPROVE') ||
      !isValidReversalReason(reason)
    ) {
      return;
    }

    setPendingAction({ kind: 'reverse', deliveryId: item.delivery_id });
    setReverseOutcome(null);

    const result = await reverseDelivery(item.delivery_id, { reason }, requestOptions);

    setPendingAction(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setReverseOutcome({ deliveryId: item.delivery_id, failure: result, delivery: null });
      return;
    }

    setReverseOutcome({ deliveryId: item.delivery_id, failure: null, delivery: result.delivery });
    setReloadToken((token) => token + 1);
  };

  const packingItems = packingUnits.status === 'ready' ? packingUnits.items : [];
  const deliveryItems = deliveries.status === 'ready' ? deliveries.items : [];

  return (
    <div className="cap-page" data-testid="deliveries">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            ส่งของ (Delivery)
            <InfoHint
              label="เกี่ยวกับหน้าส่งของ"
              text="โหลดกล่องขึ้นรถ สแกนออกจากประตู และถอนการขายเมื่อส่งของผิด — โหลดไม่ขยับสต๊อก มีแต่การสแกนออกเท่านั้นที่ของออกจากบริษัทจริง (D-105)"
            />
          </h1>
        </div>
      </div>

      {!canWriteAnything ? (
        <div className="cap-notice" data-testid="dv-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <h2 className="cap-notice__title">สิทธิ์ชุดนี้ยังโหลด/ส่ง/ถอนการขายไม่ได้</h2>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_edit</code>,{' '}
            <code className="cap-table__code">can_create</code> หรือ{' '}
            <code className="cap-table__code">can_approve</code> ของโมดูล{' '}
            <code className="cap-table__code">{DELIVERY_MODULE}</code> — รายการยังใช้ได้ตามปกติ
          </p>
        </div>
      ) : null}

      {/* --------------------------------------------------- ตัวกรอง -- */}

      <form
        className="cap-filters"
        data-testid="dv-filter-form"
        onSubmit={(event) => {
          event.preventDefault();
          setSoNumberFilter(soNumberInput);
          setCustomerCodeFilter(customerCodeInput);
        }}
      >
        <label className="cap-field">
          <span className="cap-field__label">
            เลขใบสั่งขาย
            <InfoHint
              label="เกี่ยวกับตัวกรองเลขใบสั่งขาย"
              text="ตัวกรองจริงของเซิร์ฟเวอร์ — ใช้กับทั้งสองตารางด้านล่าง"
            />
          </span>
          <input
            className="cap-input"
            type="text"
            name="dv_so_number_filter"
            data-testid="dv-filter-so-number"
            value={soNumberInput}
            maxLength={SO_NUMBER_MAX_LENGTH}
            autoComplete="off"
            onChange={(event) => {
              setSoNumberInput(event.target.value);
            }}
          />
        </label>

        <label className="cap-field">
          <span className="cap-field__label">
            รหัสลูกค้า
            <InfoHint
              label="เกี่ยวกับตัวกรองรหัสลูกค้า"
              text="ใช้กรองเฉพาะรายการเดลิเวอรีด้านล่าง"
            />
          </span>
          <input
            className="cap-input"
            type="text"
            name="dv_customer_code_filter"
            data-testid="dv-filter-customer"
            value={customerCodeInput}
            maxLength={CUSTOMER_CODE_MAX_LENGTH}
            autoComplete="off"
            onChange={(event) => {
              setCustomerCodeInput(event.target.value);
            }}
          />
        </label>

        <button type="submit" className="cap-button cap-button--ghost" data-testid="dv-filter-apply">
          กรอง
        </button>
      </form>

      {/* --------------------------------------------- กล่องที่รอโหลด/ส่ง -- */}

      <section className="cap-card cap-card--table" aria-label="กล่องที่รอโหลดหรือส่ง">
        <div className="cap-card__head">
          <h2 className="cap-card__title">กล่องที่รอโหลด/ส่ง</h2>
        </div>

        {packingUnits.status === 'loading' ? (
          <div className="cap-notice" data-testid="dv-pu-loading" role="status">
            <p className="cap-notice__text">กำลังโหลดรายการกล่อง…</p>
          </div>
        ) : null}

        {packingUnits.status !== 'loading' && packingUnits.status !== 'ready' ? (
          <FailureNotice
            failure={packingUnits}
            context="packing-units"
            testIdPrefix="dv-pu"
            onRetry={() => {
              setReloadToken((token) => token + 1);
            }}
          />
        ) : null}

        {packingUnits.status === 'ready' && packingItems.length === 0 ? (
          <div className="cap-notice" data-testid="dv-pu-empty" role="status">
            <StatusBadge tone="neutral">ยังไม่มีกล่อง</StatusBadge>
            <h2 className="cap-notice__title">ไม่มีกล่องที่ตรงกับตัวกรองนี้</h2>
          </div>
        ) : null}

        {packingUnits.status === 'ready' && packingItems.length > 0 ? (
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="dv-pu-table">
              <caption className="cap-visually-hidden">
                กล่องของโรงงานที่คุณเข้าถึงได้ พร้อมสถานะและปุ่มโหลด/ส่งที่สถานะนั้นอนุญาต
              </caption>
              <thead>
                <tr>
                  <th scope="col">เลขกล่อง</th>
                  <th scope="col">โรงงาน</th>
                  <th scope="col">ใบสั่งขาย</th>
                  <th scope="col">สินค้า</th>
                  <th scope="col">จำนวน</th>
                  <th scope="col">สถานะ</th>
                  <th scope="col">การดำเนินการ</th>
                </tr>
              </thead>
              <tbody>
                {packingItems.map((item) => {
                  const action = deliveryLifecycleActionFor(item.status);
                  const outcome =
                    packingOutcome?.packingUnitId === item.packing_unit_id &&
                    packingOutcome.failure !== null
                      ? packingOutcome
                      : null;

                  return (
                    <tr
                      key={item.packing_unit_id}
                      data-testid={`dv-pu-row-${item.package_no}`}
                      data-status={item.status}
                    >
                      <th scope="row" className="cap-table__row-head">
                        <code className="cap-table__code">{item.package_no}</code>
                      </th>
                      <td>
                        <code className="cap-table__code">{item.plant_code}</code>
                      </td>
                      <td>
                        <code className="cap-table__code">{item.so_number}</code>
                      </td>
                      <td>
                        <code className="cap-table__code">{item.product_code}</code>
                      </td>
                      <td>
                        {item.packed_qty} {item.uom_code}
                      </td>
                      <td>
                        <StatusBadge tone={packingUnitStatusTone(item.status)}>
                          {packingUnitStatusLabel(item.status)}
                        </StatusBadge>
                      </td>
                      <td>
                        {action === null ? (
                          <span className="cap-field__hint">ไม่มีการดำเนินการที่หน้าจอนี้</span>
                        ) : (
                          <PackingRowAction
                            item={item}
                            action={action}
                            permissions={permissions}
                            busy={pendingAction !== null}
                            pendingHere={
                              pendingAction?.kind === action &&
                              pendingAction.packingUnitId === item.packing_unit_id
                            }
                            onRun={() => {
                              void runPackingAction(item, action);
                            }}
                          />
                        )}

                        {outcome === null || outcome.failure === null ? null : (
                          <div data-testid="dv-pu-action-failure-slot">
                            <FailureNotice
                              failure={outcome.failure}
                              context={outcome.action}
                              testIdPrefix="dv-pu"
                            />
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {/* --------------------------------------------------- เดลิเวอรี -- */}

      <section className="cap-card cap-card--table" aria-label="รายการเดลิเวอรี">
        <div className="cap-card__head">
          <h2 className="cap-card__title">รายการเดลิเวอรี</h2>
        </div>

        {deliveries.status === 'loading' ? (
          <div className="cap-notice" data-testid="dv-dl-loading" role="status">
            <p className="cap-notice__text">กำลังโหลดรายการเดลิเวอรี…</p>
          </div>
        ) : null}

        {deliveries.status !== 'loading' && deliveries.status !== 'ready' ? (
          <FailureNotice
            failure={deliveries}
            context="deliveries"
            testIdPrefix="dv-dl"
            onRetry={() => {
              setReloadToken((token) => token + 1);
            }}
          />
        ) : null}

        {deliveries.status === 'ready' && deliveryItems.length === 0 ? (
          <div className="cap-notice" data-testid="dv-dl-empty" role="status">
            <StatusBadge tone="neutral">ยังไม่มีเดลิเวอรี</StatusBadge>
            <h2 className="cap-notice__title">ไม่มีเดลิเวอรีที่ตรงกับตัวกรองนี้</h2>
          </div>
        ) : null}

        {deliveries.status === 'ready' && deliveryItems.length > 0 ? (
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="dv-dl-table">
              <caption className="cap-visually-hidden">
                เดลิเวอรีของโรงงานที่คุณเข้าถึงได้ รวมเดลิเวอรีที่ถูกถอนแล้ว พร้อม
                เครื่องหมายและปุ่มถอนการขาย
              </caption>
              <thead>
                <tr>
                  <th scope="col">เลขเดลิเวอรี</th>
                  <th scope="col">กล่อง</th>
                  <th scope="col">ใบสั่งขาย</th>
                  <th scope="col">ลูกค้า</th>
                  <th scope="col">จำนวนที่ส่งจริง</th>
                  <th scope="col">ผู้ส่ง/เวลา</th>
                  <th scope="col">สถานะการกลับรายการ</th>
                  <th scope="col">การดำเนินการ</th>
                </tr>
              </thead>
              <tbody>
                {deliveryItems.map((item) => {
                  const isReversed = item.reversed_at !== null;
                  const outcome =
                    reverseOutcome?.deliveryId === item.delivery_id ? reverseOutcome : null;
                  const reason = reasonDrafts[item.delivery_id] ?? '';
                  const allowed = can(permissions, DELIVERY_MODULE, 'APPROVE');
                  const validReason = isValidReversalReason(reason);
                  const busy = pendingAction !== null;

                  return (
                    <tr
                      key={item.delivery_id}
                      data-testid={`dv-dl-row-${item.delivery_no}`}
                      data-reversed={isReversed}
                    >
                      <th scope="row" className="cap-table__row-head">
                        <code className="cap-table__code">{item.delivery_no}</code>
                      </th>
                      <td>
                        <code className="cap-table__code">{item.package_no}</code>
                      </td>
                      <td>
                        <code className="cap-table__code">{item.so_number}</code>
                      </td>
                      <td>
                        <code className="cap-table__code">{item.customer_code}</code>
                      </td>
                      <td>
                        {/* shipped_qty ไม่ขยับไม่ว่ากรณีใด แม้ถูกถอนการขายแล้ว
                            (D-105 ข้อ 6) — สิ่งที่ออกจากโรงงานวันนั้นเป็นข้อเท็จจริง
                            ที่แยกจากการถอน */}
                        {item.shipped_qty} {item.uom_code}
                      </td>
                      <td>
                        {item.dispatched_by_employee_id} / {item.dispatched_at}
                      </td>
                      <td>
                        {isReversed ? (
                          <div data-testid={`dv-dl-reversed-${item.delivery_no}`}>
                            <StatusBadge tone="danger">ถูกถอนการขายแล้ว</StatusBadge>
                            <p className="cap-notice__note">
                              เมื่อ {item.reversed_at} โดย {item.reversed_by_employee_id}
                              <br />
                              เหตุผล: {item.reversal_reason}
                            </p>
                          </div>
                        ) : (
                          <StatusBadge tone="success">ยืนอยู่ (ยังไม่ถูกถอน)</StatusBadge>
                        )}
                      </td>
                      <td>
                        {isReversed ? (
                          <span className="cap-field__hint">ถอนไปแล้ว ไม่มีการดำเนินการเพิ่ม</span>
                        ) : (
                          <div>
                            <label className="cap-field">
                              <span className="cap-field__label">เหตุผลที่ถอนการขาย</span>
                              <input
                                className="cap-input"
                                data-testid={`dv-reverse-reason-${item.delivery_no}`}
                                value={reason}
                                maxLength={REVERSAL_REASON_MAX_LENGTH}
                                autoComplete="off"
                                disabled={busy}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  setReasonDrafts((previous) => ({
                                    ...previous,
                                    [item.delivery_id]: value,
                                  }));
                                }}
                              />
                            </label>
                            <button
                              type="button"
                              className="cap-button cap-button--ghost"
                              data-testid={`dv-action-reverse-${item.delivery_no}`}
                              disabled={busy || !allowed || !validReason}
                              title={
                                !allowed
                                  ? `ต้องมีสิทธิ์ ${DELIVERY_MODULE} + APPROVE`
                                  : !validReason
                                    ? 'ต้องกรอกเหตุผลก่อนจึงจะถอนการขายได้'
                                    : undefined
                              }
                              onClick={() => {
                                void runReverse(item);
                              }}
                            >
                              {pendingAction?.kind === 'reverse' &&
                              pendingAction.deliveryId === item.delivery_id
                                ? 'กำลังถอน…'
                                : 'ถอนการขาย (Reverse)'}
                            </button>
                            {allowed ? null : (
                              <span
                                className="cap-field__hint"
                                data-testid={`dv-action-reason-reverse-${item.delivery_no}`}
                              >
                                ต้องมีสิทธิ์ {DELIVERY_MODULE} + APPROVE
                              </span>
                            )}
                            {allowed && !validReason ? (
                              <span
                                className="cap-field__hint"
                                data-testid={`dv-reverse-reason-hint-${item.delivery_no}`}
                              >
                                ต้องกรอกเหตุผลก่อนปุ่มจะกดได้
                              </span>
                            ) : null}
                          </div>
                        )}

                        {outcome === null || outcome.failure === null ? null : (
                          <div data-testid="dv-dl-action-failure-slot">
                            <FailureNotice
                              failure={outcome.failure}
                              context="reverse"
                              testIdPrefix="dv-dl"
                            />
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
}
